import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import { useMsal } from '@azure/msal-react';
import BounceLoader from './ui/Loaders/BounceLoader';
import { getValidToken } from '../utils';

/* Local Type Imports */
import { CHAT_TAB_VIEWS, PAGE_VIEW, ClassNameEvent, UpdatingChatHistoryState, SourceType } from '../types';

/* Local Context Imports */
import { APIContext } from '../contexts/APIContext';
import { ChatbotContext } from '../contexts/ChatbotContext';
import { SessionContext } from '../contexts/SessionContext';

/* Third-Party Chat Library Imports */
import Chatbot from 'react-chatbot-kit';
import 'react-chatbot-kit/build/main.css';
import '@radix-ui/react-tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { TooltipContent } from '@radix-ui/react-tooltip';

/* Local Chat Library Customizations */
import '../chatbot/customStyles.css';
import config from '../chatbot/config';
import MessageParser from '../chatbot/MessageParser';
import ActionProvider from '../chatbot/ActionProvider';

/* Local Component Imports */
import { ResetModal } from './ResetModal';
import { DisclaimerModal } from './DisclaimerModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/Tabs';
import { Tooltip, TooltipProvider, TooltipTrigger } from './ui/Tooltip';
import { Toggle } from './ui/Toggle';
import { ResponseDetails } from './ResponseDetails';
import { StoryBuilder } from './StoryBuilder';
import { ChatHistoryPanel } from './ChatHistoryPanel';
import ChatbotInput from './ChatbotInput';

/* Local Hook Imports */
import useOutsideClickHandler from '../hooks/useOutsideClickHandler';

/* Local Utility Imports */
import { fetchChatHistory, fetchProjectDetails, trackUserClicks } from '../utils';
import { suggestedResponsesImg } from '../chatbot/chatUtils';

/* Local Custom Hook Imports */
import { useToast } from './ui/toast/UseToast';
import Usersurvey from './Usersurvey';

export const ChatComponent = (props: any) => {
  const { toggleSideBarHandler } = props;
  const {
    resetApp,
    setResetApp,
    activeChatTab,
    setActiveChatTab,
    setSelectedProject,
    selectedTranscript,
    setSelectedTranscript,
    selectedResponseId,
    chatPromptResponseHistory,
    showChatHistoryPanel,
    setShowChatHistoryPanel,
    showRightPanel,
    setShowRightPanel,
    feedbackSubmit,
    setFeedbackSubmit,
    prompt,
    setPrompt,
    fetchChatHistoryFlag,
    setFetchChatHistoryFlag,
  } = useContext(ChatbotContext);

  // Flag from WebsocketContext to control whether Loading page is shown or not
  const {
    websocketConnected,
    chatSessionId,
    userDetails,
    websocketConnectionRef: { current: websocket },
    projectNames,
    chatHistoryList,
    saveUserDetails,
    isChatHistorySelected,
    setIsChatHistorySelected,
    surveyMaster,
    setSurveyMaster,
    WBUId,
    AppName,
    accounts,
    batchType,
  } = useContext(APIContext);
  const { pageView, setPageView, setOpenAI, setGenAI, isPromptGenerating, setIsUpdatingChatHistory } =
    useContext(SessionContext);

  // State for Reset Modal
  const [showResetModal, setShowResetModal] = useState<boolean>(false);

  // Ref for suggested responses (lightbulb) button
  const suggestedResponseBtnRef = useRef<boolean>(false);

  // Ref for unique responseId from chat response
  const prevSelectedResponseIdRef = useRef(selectedResponseId);

  // State for dropdown and search input
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [searchProjectInput, setSearchProjectInput] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);

  // State for hover effects
  const [isHovered, setIsHovered] = useState(false);
  const [hoveringProject, setHoveringProject] = useState<string | null>(null);
  const [hoveringAll, setHoveringAll] = useState<boolean>(false);
  const [toasterMessage, setToasterMessage] = useState('');

  // Toast hook from context
  const { toast } = useToast();

  // MSAL accounts
  // const { accounts } = useMsal();

  // State for showing disclaimer modal
  const [showDisclaimerModal, setShowDisclaimerModal] = useState<boolean>(false);

  // Project names
  const projects = projectNames || [];

  // Character count state
  const [countChar, setCountChar] = useState<number>(0);

  // State for select Transcript dropdown
  const [selectOpen, setSelectOpen] = useState<boolean>(false);
  const [panelWidth, setPanelWidth] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, width: 0 }); // Track starting position

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      width: panelWidth,
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !panelRef.current) return;

      const parentElement = panelRef.current.parentElement;
      if (!parentElement) return;

      const containerWidth = parentElement.offsetWidth;
      const deltaX = dragStartRef.current.x - e.clientX;
      // Convert pixel movement to percentage
      const deltaPercent = (deltaX / containerWidth) * 100;
      // Calculate new width based on starting width + change
      const newWidth = dragStartRef.current.width + deltaPercent;
      const clampedWidth = Math.min(Math.max(newWidth, 40), 70);
      setPanelWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ew-resize';
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, panelWidth]);

  const handleClickOutside = (): void => {
    setDropdownOpen(false);
  };

  const handleSelectClickOutside = (): void => {
    setSelectOpen(false);
  };

  useOutsideClickHandler('project-dropdown', handleClickOutside);
  useOutsideClickHandler('transcript-dropdown', handleSelectClickOutside);

  const handleDisclaimerModalClose = () => {
    setShowDisclaimerModal(false);
    setToasterMessage('For Internal Use for business development purposes.');
    showToaster('InfoNudge');
    localStorage.setItem('hasLegalDisclaimer', 'true');
  };

  const handleNavbarClick = async (view: PAGE_VIEW) => {
    const token = await getValidToken();
    trackUserClicks(websocket, 'GMA Landing Page', userDetails, chatSessionId, WBUId, AppName, accounts, token);
    if (view === pageView) {
      setPageView(PAGE_VIEW.LANDING);
    } else {
      setPageView(view);
    }
  };

  useEffect(() => {
    if (websocket && websocket.state === 'Connected' && !projectNames.length) {
      // trigger fetch project details only if projectNames list is empty or not fetched.
      (async () => {
        const token = await getValidToken();
        fetchProjectDetails(accounts, websocket, token);
        // trigger fetch history
        fetchChatHistory(accounts, websocket, token);
      })();
    }
  }, [websocket, websocketConnected]);

  useEffect(() => {
    // const hasShownDisclaimer = localStorage.getItem('hasShownDisclaimer');
    // const disclaimer = localStorage.getItem('disclaimer');
    // if (!hasShownDisclaimer && !disclaimer) {
    //   setShowDisclaimerModal(true);
    // }
    const console = !!(AppName === 'GenAIOnConsole');
    if (!console && !WBUId) {
      if (!localStorage.getItem('hasLegalDisclaimer')) {
        setShowDisclaimerModal(true);
      }
    } else if (!localStorage.getItem('hasInformationalNudge') && (WBUId || console)) {
      localStorage.setItem('hasInformationalNudge', 'true');
      setToasterMessage('For Internal Use for business development purposes.');
      showToaster('InfoNudge');
    }
  }, [AppName, WBUId]);

  function toggleOpenAI(value: boolean) {
    setGenAI(false);
    setOpenAI(value);
  }

  function toggleGenAI(value: boolean) {
    setOpenAI(false);
    setGenAI(value);
  }

  // Using an effect to set the max length of the chat input along with adding the elements for the suggested responses button and the send icon
  useEffect(() => {
    if (websocketConnected) {
      const inputEl = document.querySelector('.react-chatbot-kit-chat-input') as HTMLInputElement; //inputbox************

      if (inputEl) {
        inputEl.setAttribute('maxlength', '2000');
        // inputEl.addEventListener('keyup', showCharacterCount); //comments due to custom chat input box implementation
      } else {
        console.error('Input element not found');
      }
      if (!suggestedResponseBtnRef.current) {
        const submitBtn = document.querySelector('.react-chatbot-kit-chat-btn-send'); // Get submit button**********

        if (submitBtn) {
          submitBtn.setAttribute('type', 'submit'); // ensure button is submit type to submit form
          const sendImg = document.createElement('img'); // send icon image element
          sendImg.setAttribute('class', 'send-msg-icon w-4 h-4');
          sendImg.setAttribute('src', '/icons/paperplane.svg');
          sendImg.setAttribute('alt', 'Send message');
          sendImg.setAttribute('id', 'chat_send_icon');
          submitBtn.appendChild(sendImg); // Add the image to the button
          submitBtn.addEventListener('click', () => setCountChar(0));
        }

        const parentInput = document.querySelector('.react-chatbot-kit-chat-input-form'); // Get parent element of input*

        if (submitBtn && parentInput) {
          // Create a new parent div element to hold the buttons
          const newBtnsParentEl = document.createElement('div');
          newBtnsParentEl.setAttribute('class', 'new-btns-parent-container flex flex-row items-center pl-3');
          newBtnsParentEl.setAttribute('id', 'chat_img_container');

          //OpenAI container
          const sourceButtonContainer = document.createElement('div');
          sourceButtonContainer.setAttribute('class', 'tooltip');

          // OpenAI
          const SourceButton = document.createElement('button');
          SourceButton.setAttribute('class', 'source-btn w-9 mr-1 h-full flex items-center justify-center mb:hidden');
          SourceButton.setAttribute('id', 'source-btn-id');
          SourceButton.setAttribute('type', 'button');
          const SourceImg = document.createElement('img');
          let OpenAI = false;
          SourceImg.setAttribute('src', '/icons/OpenAI.svg');
          SourceImg.setAttribute('alt', 'source');
          SourceImg.setAttribute('class', 'w-43 h-43');
          SourceImg.setAttribute('id', 'img_source_id');
          SourceImg.setAttribute('draggable', 'false');
          SourceButton.appendChild(SourceImg);

          //GenAI container
          const genAIButtonContainer = document.createElement('div');
          genAIButtonContainer.setAttribute('class', 'tooltip');

          // GenAI
          const GenAIButton = document.createElement('button');
          GenAIButton.setAttribute('class', 'source-btn w-9 mr-1 h-full flex items-center justify-center mb:hidden');
          GenAIButton.setAttribute('id', 'gen-btn-id');
          GenAIButton.setAttribute('type', 'button');
          const GenImg = document.createElement('img');
          let GenAI = false;
          GenImg.setAttribute('src', '/icons/genAI.svg');
          GenImg.setAttribute('alt', 'genAI');
          GenImg.setAttribute('class', 'w-43 h-43');
          GenImg.setAttribute('id', 'img_gen_id');
          GenImg.setAttribute('draggable', 'false');
          GenAIButton.appendChild(GenImg);

          //genAi tooltip text
          const genAIButtonTooltipText = document.createElement('span');
          genAIButtonTooltipText.textContent = SourceType.GENAI;
          genAIButtonTooltipText.setAttribute(
            'class',
            'tooltiptext translate-x-[-52%] translate-y-[-190%] wrap h-[50px] w-[200px]'
          );

          //add genAi btn as child to container
          genAIButtonContainer.appendChild(GenAIButton);
          //add genAi tooltiptext as child to container
          genAIButtonContainer.appendChild(genAIButtonTooltipText);

          SourceButton.addEventListener('click', () => {
            OpenAI = !OpenAI;
            SourceImg.setAttribute('src', OpenAI ? 'icons/activeOpenAI.svg' : 'icons/OpenAI.svg');
            if (GenAI) {
              GenImg.setAttribute('src', OpenAI ? 'icons/genAI.svg' : 'icons/activeGenAI.svg');
              GenAI = false;
            }
            toggleOpenAI(OpenAI);
          });
          GenAIButton.addEventListener('click', () => {
            GenAI = !GenAI;
            GenImg.setAttribute('src', GenAI ? 'icons/activeGenAI.svg' : 'icons/genAI.svg');
            if (OpenAI) {
              SourceImg.setAttribute('src', GenAI ? 'icons/OpenAI.svg' : 'icons/activeOpenAI.svg');
              OpenAI = false;
            }
            // window.dispatchEvent(new CustomEvent('source-btn-clicked'));
            toggleGenAI(GenAI);
          });

          // Event listeners for hover effect
          SourceButton.addEventListener('mouseenter', () => {
            if (!OpenAI) {
              SourceImg.setAttribute('src', 'icons/activeOpenAI.svg');
            }
          });
          SourceButton.addEventListener('mouseleave', () => {
            if (!OpenAI) {
              SourceImg.setAttribute('src', 'icons/OpenAI.svg');
            }
          });

          GenAIButton.addEventListener('mouseenter', () => {
            if (!GenAI) {
              GenImg.setAttribute('src', 'icons/activeGenAI.svg');
            }
          });
          GenAIButton.addEventListener('mouseleave', () => {
            if (!GenAI) {
              GenImg.setAttribute('src', 'icons/genAI.svg');
            }
          });

          //OpenAIBtn tooltip
          const sourceButtonTooltipText = document.createElement('span');
          sourceButtonTooltipText.textContent = 'Azure OpenAI';
          sourceButtonTooltipText.setAttribute('class', 'tooltiptext translate-x-[-52%] translate-y-[-230%]');

          //Attach openAI btn to openAi container
          sourceButtonContainer.appendChild(SourceButton);

          //Attach openAi tooltip to openAi container
          sourceButtonContainer.appendChild(sourceButtonTooltipText);

          //SuggestedResponseBtn Container
          const suggestedResponsesButtonContainer = document.createElement('div');
          suggestedResponsesButtonContainer.setAttribute('class', 'tooltip');

          // Suggested Responses button
          const suggestedResponsesButton = document.createElement('button');
          suggestedResponsesButton.setAttribute(
            'class',
            'suggested-responses-btn w-9 h-full flex items-center justify-center'
          );
          suggestedResponsesButton.setAttribute('id', 'suggested-responses-btn-id');
          suggestedResponsesButton.setAttribute('type', 'button');
          suggestedResponsesImg.setAttribute('src', '/icons/BulbIcon.svg');
          suggestedResponsesImg.setAttribute('alt', 'suggestedResponses');
          suggestedResponsesImg.setAttribute('class', 'w-44');
          suggestedResponsesImg.setAttribute('draggable', 'false');
          suggestedResponsesButton.appendChild(suggestedResponsesImg);
          suggestedResponsesButton.addEventListener('click', () => {
            window.dispatchEvent(new CustomEvent('suggested-response-btn-clicked'));
            suggestedResponsesImg.setAttribute('src', 'icons/activeBulbIcon.svg');
          });
          //SuggestedResponseBtn tooltip
          const suggestedResponsesButtonTooltipText = document.createElement('span');
          suggestedResponsesButtonTooltipText.textContent = 'Suggested Questions';
          suggestedResponsesButtonTooltipText.setAttribute(
            'class',
            'tooltiptext translate-x-[-50%] translate-y-[-190%] wrap h-[50px] w-[90px]'
          );

          //Attach suggestedResponsesButton to Container
          suggestedResponsesButtonContainer.appendChild(suggestedResponsesButton);
          //Attach tooltip to suggestedResponsesButton Container
          suggestedResponsesButtonContainer.appendChild(suggestedResponsesButtonTooltipText);

          suggestedResponsesButton.addEventListener('mouseenter', () => {
            suggestedResponsesImg.setAttribute('src', 'icons/activeBulbIcon.svg');
          });
          suggestedResponsesButton.addEventListener('mouseleave', () => {
            const responseSuggestionsPopupContainers = document.querySelectorAll(
              '.response-suggestions-popup-container'
            );

            if (responseSuggestionsPopupContainers.length === 0) {
              suggestedResponsesImg.setAttribute('src', '/icons/BulbIcon.svg');
            } else {
              responseSuggestionsPopupContainers.forEach((button) => {
                if (button.classList.contains('hidden')) {
                  suggestedResponsesImg.setAttribute('src', '/icons/BulbIcon.svg');
                }
              });
            }
          });
          //container for newChatImg and Tooltip
          const newChatImgContainer = document.createElement('div');
          newChatImgContainer.setAttribute('class', 'tooltip');

          // New chat icon
          const newChatImg = document.createElement('img');
          newChatImg.setAttribute('src', '/icons/chat-round.svg');
          newChatImg.setAttribute('alt', 'new chat');
          newChatImg.setAttribute('id', 'newchat_icon');
          newChatImg.setAttribute(
            'class',
            `w-auto h-full pr-3 bg-[#FAFAFA] cursor-pointer mb:hidden ${
              chatPromptResponseHistory && chatPromptResponseHistory.length === 0 && !isChatHistorySelected
                ? 'not-clickable'
                : ''
            }`
          );
          newChatImg.setAttribute('draggable', 'false');
          newChatImg.addEventListener('mouseenter', () => {
            newChatImg.setAttribute('src', 'icons/activechatIconRound.svg');
          });
          newChatImg.addEventListener('mouseleave', () => {
            newChatImg.setAttribute('src', '/icons/chat-round.svg');
          });

          newChatImg.addEventListener('click', () => {
            setShowResetModal(true);
          });

          //tooltip for newChatImg
          const newChatImgTooltipText = document.createElement('span');
          newChatImgTooltipText.textContent = 'Start New Chat';
          newChatImgTooltipText.setAttribute(
            'class',
            'tooltiptext translate-x-[-56%] translate-y-[-205%] wrap h-[46px] w-[85px]'
          );

          //Add newchatImg as child to container
          newChatImgContainer.appendChild(newChatImg);
          //Attach tooltip as child to newChatImg element
          newChatImgContainer.appendChild(newChatImgTooltipText);

          // Append the buttons and newChatImg to the newBtnsParentEl
          if (submitBtn) {
            parentInput.appendChild(submitBtn);
          }
          newBtnsParentEl.appendChild(genAIButtonContainer);
          newBtnsParentEl.appendChild(sourceButtonContainer);
          newBtnsParentEl.appendChild(suggestedResponsesButtonContainer);

          // parent node of textbox
          const inputParent = parentInput.parentNode;
          if (inputParent) {
            inputParent.insertBefore(newChatImgContainer, parentInput);
            inputParent.insertBefore(newBtnsParentEl, parentInput.nextSibling);
          }
          suggestedResponseBtnRef.current = true;
        }
      }
    }
  }, [websocketConnected, resetApp]);

  useEffect(() => {
    const chatInputContainer = document.querySelector('.react-chatbot-kit-chat-input-container') as HTMLDivElement;
    const newChatImg = document.querySelector('#newchat_icon') as HTMLImageElement; // new chat Image element
    const inputEl = document.querySelector('.react-chatbot-kit-chat-input') as HTMLTextAreaElement; //inputbox************
    const submitBtn = document.querySelector('.react-chatbot-kit-chat-btn-send') as HTMLButtonElement; // Get submit button**********
    const openAIBtn = document.querySelector('#source-btn-id') as HTMLButtonElement; // get openAI button
    const genAIBtn = document.querySelector('#gen-btn-id') as HTMLButtonElement; // get genAI button
    const suggestedResponseBtn = document.querySelector('#suggested-responses-btn-id') as HTMLButtonElement; // Get suggested response bulb button**********
    const elements: HTMLElement[] = [];
    elements.push(newChatImg);
    elements.push(inputEl);
    elements.push(submitBtn);
    elements.push(openAIBtn);
    elements.push(genAIBtn);
    elements.push(suggestedResponseBtn);
    if (chatInputContainer) {
      if (isPromptGenerating) {
        inputEl.disabled = true; // prevent from receiving inputs and key press events
        elements.map((element) => handleHTMLClassName(element, ClassNameEvent.ADD, 'not-clickable')); //make elements not clickable or disabled behaviuor
      } else {
        inputEl.disabled = false;
        elements.map((element) => handleHTMLClassName(element, ClassNameEvent.REMOVE, 'not-clickable')); //make elements clickable
        if (chatPromptResponseHistory && chatPromptResponseHistory.length == 0 && !isChatHistorySelected) {
          handleHTMLClassName(newChatImg, ClassNameEvent.ADD, 'not-clickable'); //make chat Icon not clickable
        }
      }
      // Handle batch completion separately - only disable input and submit button
      if (batchType == 'batch') {
        inputEl.disabled = true;
        handleHTMLClassName(inputEl, ClassNameEvent.ADD, 'not-clickable');
        handleHTMLClassName(submitBtn, ClassNameEvent.ADD, 'not-clickable');
      } else if (!isPromptGenerating) {
        // Only re-enable if prompt is not generating
        inputEl.disabled = false;
        handleHTMLClassName(inputEl, ClassNameEvent.REMOVE, 'not-clickable');
        handleHTMLClassName(submitBtn, ClassNameEvent.REMOVE, 'not-clickable');
      }
    }
  }, [isPromptGenerating, chatPromptResponseHistory, isChatHistorySelected, batchType]);

  useEffect(() => {
    injectInputBox();
  }, [websocketConnected, resetApp]);

  /*
  function to inject the custom chat-input react component to dom and render it.
  replaces the default chat-input-box provided by chat library.
  */
  const injectInputBox = () => {
    const inputEl = document.querySelector('.react-chatbot-kit-chat-input-form') as HTMLFormElement;
    if (inputEl) {
      const root = ReactDOM.createRoot(inputEl);
      root.render(<ChatbotInput prompt={prompt} updatePrompt={updatePrompt} updateInputCount={setCountChar} />);
    }
  };

  // function to update the prompt/message which binds with custom chat-input box
  const updatePrompt = (prompt: string) => {
    setPrompt(prompt);
    showCharacterCount(prompt);
  };

  // Function to handle clicks on the reset button - initiates a reset of the app
  const resetAppFn = () => {
    setResetApp(true);
    setOpenAI(false);
    setGenAI(false);
    suggestedResponseBtnRef.current = false;
    const chatIcon = document.getElementById('newchat_icon');
    chatIcon?.remove();
    const sendIcon = document.getElementById('chat_send_icon');
    sendIcon?.remove();
    const chatImgContainer = document.getElementById('chat_img_container');
    chatImgContainer?.remove();
  };

  // Callback function for when the reset modal is closed
  const handleModalClose = (reset: boolean) => {
    if (reset) {
      resetAppFn();
    }
  };

  //Function to add/append or remove classnames to html elements
  const handleHTMLClassName = (element: HTMLElement, event: ClassNameEvent, className: string): void => {
    switch (event) {
      case ClassNameEvent.ADD:
        element.classList.add(className);
        break;
      case ClassNameEvent.REMOVE:
        element.classList.remove(className);
        break;
    }
  };

  useEffect(() => {
    if (projects.length) {
      resetProjects();
    }
  }, [projects]);

  const resetProjects = () => {
    setSelectedProjects(projects);
    updateSelectedProject(projects);
  };

  const handleSelectedProject = (projectName: string) => {
    setSelectedProjects((prevSelectedProjects) => {
      const updatedProjects = prevSelectedProjects.includes(projectName)
        ? prevSelectedProjects.filter((project) => project !== projectName)
        : [...prevSelectedProjects, projectName];
      updateSelectedProject(updatedProjects);
      return updatedProjects;
    });
  };

  const handleSelectAll = () => {
    const updatedProjects = selectedProjects.length === projects.length ? [] : projects;
    setSelectedProjects(updatedProjects);
    updateSelectedProject(updatedProjects);
  };

  const toggleDropdown = () => {
    if (!dropdownOpen) setSearchProjectInput('');
    setDropdownOpen(!dropdownOpen);
  };

  const toggleSelect = () => {
    setSelectOpen(!selectOpen);
  };

  const handleSelectTranscript = (dropDownValue: string) => {
    setSelectedTranscript(dropDownValue);
    setSelectOpen(false);
  };

  const filteredProjects =
    projects?.filter((project: string) => project?.toLowerCase().includes(searchProjectInput?.toLowerCase())) || [];

  const updateSelectedProject = (selectedProject: string[]) => {
    setSelectedProject(
      selectedProject.length === 0
        ? []
        : projects?.length === selectedProject.length
        ? ['All Project']
        : selectedProject
    );
  };

  const isAllSelected = selectedProjects.length === projects?.length && selectedProjects.length !== 0;

  const showCharacterCount = (prompt: string) => {
    const charLength = prompt.length;
    setCountChar(charLength);
  };

  // Actions to be performed when History Panel Button is clicked
  const handleChatHistoryPanel = useCallback(async () => {
    const token = await getValidToken();
    setShowChatHistoryPanel(true);
    setShowRightPanel(false);
    trackUserClicks(websocket, 'Chat-History', userDetails, chatSessionId, WBUId, AppName, accounts, token);
  }, [websocket, userDetails, chatSessionId]);

  // Actions to be performed when Right Panel Button is clicked
  const handleRightPanel = useCallback(() => {
    setShowChatHistoryPanel(false);
    setShowRightPanel(!showRightPanel);
  }, [showRightPanel]);

  // Toaster message on rename or delete in chat history
  const showToaster = (action: string) => {
    toast({
      status:
        (action === 'InfoNudge' && 'info') ||
        (action === 'rename' && 'success') ||
        (action === 'delete' && 'success') ||
        'success',
      description:
        (action === 'InfoNudge' && 'For Internal Use for business development purposes.') ||
        (action === 'rename' && 'Saved successfully') ||
        (action === 'delete' && 'Deleted successfully'),
    });
  };

  //fetch chat history list when websocket is connected and chat response or feedbackis updated
  useEffect(() => {
    if (websocket && websocket.state === 'Connected') {
      if (
        (prevSelectedResponseIdRef.current !== selectedResponseId && fetchChatHistoryFlag === true) ||
        feedbackSubmit
      ) {
        (async () => {
          const token = await getValidToken();
          fetchChatHistory(accounts, websocket, token);
        })();
        prevSelectedResponseIdRef.current = selectedResponseId;
      }
    }
    setFetchChatHistoryFlag(true);
  }, [websocket, websocketConnected, selectedResponseId, saveUserDetails]);

  // Remove loader when chatHistoryList updated or Remove overlay when feedback is updated
  useEffect(() => {
    setIsUpdatingChatHistory((prev: UpdatingChatHistoryState) => {
      if (prev.action) {
        showToaster(prev.action);
      }
      return { state: false, action: '' };
    });
    setFeedbackSubmit(false);
  }, [chatHistoryList]);

  const handleSideBar = () => {
    toggleSideBarHandler((prev: boolean) => !prev);
  };

  return (
    <>
      {/* Hide Disclaimer modal & User Survey Modal when in iframe and when WBUId is not empty */}
      {WBUId !== '' && window.self !== window.top ? null : (
        <>
          {showDisclaimerModal && (
            <DisclaimerModal
              showDisclaimerModal={showDisclaimerModal}
              setShowDisclaimerModal={setShowDisclaimerModal}
              onModalClose={handleDisclaimerModalClose}
            />
          )}
          <Usersurvey openUserSurvey={surveyMaster} setOpenUserSurvey={setSurveyMaster} />
        </>
      )}
      {/* Reset confirmation modal */}
      <ResetModal
        showResetModal={showResetModal}
        setShowResetModal={setShowResetModal}
        onModalClose={handleModalClose}
        resetProjects={resetProjects}
        handleSelectTranscript={handleSelectTranscript}
        setIsChatHistorySelected={setIsChatHistorySelected}
      />
      {/* If websocket is connected, show chat page. Else, show loader */}
      {websocketConnected ? (
        // Chat Page
        <div className={`chat-page flex flex-row h-full w-full relative ${!showRightPanel && 'overflow-hidden'}`}>
          {/* ChatHistory component */}
          <ChatHistoryPanel
            showChatHistoryPanel={showChatHistoryPanel}
            setShowChatHistoryPanel={setShowChatHistoryPanel}
            chatHistoryList={chatHistoryList}
            isPromptGenerating={isPromptGenerating}
          />

          {/* Chatbot component */}
          <div className="chat-container relative flex h-full w-full flex-col flex-nowrap bg-[#F5F5F5]">
            <div className="chatbot-container min-h-0 absolute z-[1] px-6 w-full">
              {/* New div for icons and text */}
              <div className="flex justify-between items-center mt-[5px] mr-4 bg-[#F5F5F5] mb:flex-col mb:items-start mb:mr-0">
                <div className="flex items-center gap-3 ml-4">
                  <div className="hidden tb:block">
                    <img
                      onClick={handleSideBar}
                      src={'icons/icon-hamburger.svg'}
                      className={`w-4 min-w-[16px] h-4 cursor-pointer
                              ${isPromptGenerating && 'not-clickable'} 
                            `}
                    />
                  </div>
                  {/*isPromptGenerating changes should be reverted back for chat Icon/btn, once chatHistory feature
                  implements*/}
                  <div className="tb:hidden">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Toggle
                            className={`!bg-transparent ${isPromptGenerating && 'cursor-default'} ${
                              showChatHistoryPanel && 'hidden'
                            }`}
                          >
                            <img
                              onClick={handleChatHistoryPanel}
                              src={isHovered ? '/icons/icon-chathistory-active.svg' : 'icons/icon-chathistory.svg'}
                              className={`min-w-[32px] h-7
                              ${isPromptGenerating && 'not-clickable'} 
                            `}
                              onMouseEnter={() => setIsHovered(true)}
                              onMouseLeave={() => setIsHovered(false)}
                              draggable={false}
                            />
                          </Toggle>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="bg-[#505050] border-0 max-w-[218px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2"
                          sideOffset={5}
                          align={'center'}
                          alignOffset={0}
                        >
                          <p className="tooltip-content text-white text-xs capitalize not-italic">Chat History</p>
                          <TooltipPrimitive.Arrow width={11} height={5} style={{ fill: '#505050' }} />
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <img
                            src="/icons/HomeIcon.svg"
                            className={`min-w-[24px] h-6 p-1 cursor-pointer mt-1 ${
                              isPromptGenerating && 'not-clickable cursor-default'
                            }`}
                            onClick={() => handleNavbarClick(PAGE_VIEW.LANDING)}
                            draggable={false}
                          />
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="bg-[#505050] border-0 max-w-[218px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2"
                          sideOffset={11}
                          align={'center'}
                          alignOffset={0}
                        >
                          <p className="tooltip-content text-white text-xs capitalize not-italic">Home</p>
                          <TooltipPrimitive.Arrow width={11} height={5} style={{ fill: '#505050' }} />
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="font-graphik text-base text-black font-medium leading-normal p-2 pl-1 text-center mb-1">
                    GMA
                  </p>
                </div>
                <div className="flex items-center gap-4 mb:ml-4">
                  <div className="flex items-center gap-6 flex-wrap justify-end flex-1 mb:gap-0 mb:justify-start">
                    <div className={`inline-block ${isPromptGenerating && 'not-clickable'}`}>
                      <div className="relative text-start" id="project-dropdown">
                        <label className="text-sm font-normal">Projects :</label>
                        <input
                          type="text"
                          className={`project-input w-[120px] pl-2 text-sm text-start relative bg-[#F5F5F5] cursor-pointer ${
                            selectedProjects.length === 0 ? 'font-light' : 'font-medium '
                          }`}
                          value={
                            selectedProjects.length === 0
                              ? 'Select'
                              : selectedProjects.length === projects.length
                              ? 'All Selected'
                              : `${selectedProjects.length} Selected`
                          }
                          onChange={(e) => setSearchProjectInput(e.target.value)}
                          onClick={toggleDropdown}
                          readOnly
                        />
                        <img
                          className={`ml-40 w-3.5 absolute top-1/3 transition-all duration-300 ease-[ease] cursor-pointer ${
                            dropdownOpen ? 'rotate-180' : ''
                          }`}
                          src="/icons/Down.svg"
                          alt="arrow"
                          onClick={toggleDropdown}
                        />
                        {dropdownOpen && (
                          <div className="text-start text-xs bg-[#FFFFFF] w-max mt-2 absolute ml-[-70px] z-[1] mb:ml-0">
                            <div className="text-sm flex h-[40px] items-center shadow-sm">
                              <img className="ml-6" alt="Search" src="icons/Search.svg"></img>
                              <input
                                type="text"
                                placeholder="Search..."
                                value={searchProjectInput}
                                onChange={(e) => setSearchProjectInput(e.target.value)}
                                className="text-sm w-full p-2 outline-none"
                              />
                            </div>
                            {searchProjectInput === '' && (
                              <div
                                className={`text-sm flex items-center pl-6 hover:bg-[#F5F5F5] cursor-pointer ${
                                  isAllSelected ? 'text-[#7500C0]' : ''
                                }`}
                                onMouseEnter={() => setHoveringAll(true)}
                                onMouseLeave={() => setHoveringAll(false)}
                              >
                                <input
                                  type="checkbox"
                                  checked={isAllSelected}
                                  onChange={handleSelectAll}
                                  className="mr-2 opacity-0"
                                />
                                <img
                                  className="z-9 ml-[-25px]"
                                  alt="checkbox"
                                  src={
                                    isAllSelected
                                      ? '/icons/CheckboxChecked.svg'
                                      : hoveringAll
                                      ? '/icons/activeUncheckedBox.svg'
                                      : '/icons/CheckboxUnchecked.svg'
                                  }
                                  onClick={handleSelectAll}
                                />
                                <label
                                  className={`w-full text-left px-5 py-2.5 ${hoveringAll ? 'text-[#7500C0]' : ''}`}
                                >
                                  All Projects
                                </label>
                              </div>
                            )}
                            <div className="Scrollbar max-h-[198px]">
                              {filteredProjects.map((project: string, index: number) => (
                                <div
                                  key={index}
                                  className={`text-sm flex items-center hover:bg-[#F5F5F5] pl-6 cursor-pointer ${
                                    selectedProjects.includes(project) ? 'text-[#7500C0]' : 'checkbox-hover'
                                  }`}
                                  onMouseEnter={() => setHoveringProject(project)}
                                  onMouseLeave={() => setHoveringProject(null)}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedProjects.includes(project)}
                                    onChange={() => handleSelectedProject(project)}
                                    className="mr-2 opacity-0"
                                  />
                                  <img
                                    className="z-9 ml-[-25px]"
                                    alt="checkbox"
                                    src={
                                      selectedProjects.includes(project)
                                        ? '/icons/CheckboxChecked.svg'
                                        : hoveringProject === project
                                        ? '/icons/activeUncheckedBox.svg'
                                        : '/icons/CheckboxUnchecked.svg'
                                    }
                                    onClick={() => handleSelectedProject(project)}
                                  />
                                  <label
                                    className={`w-full text-left px-5 py-2.5 ${
                                      hoveringProject === project ? 'text-[#7500C0]' : ''
                                    }`}
                                  >
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          {project.length > 17 ? `${project.slice(0, 17)}...` : project}
                                        </TooltipTrigger>
                                        <TooltipContent
                                          side="top"
                                          className="bg-[#505050] border-0 max-w-[218px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2 rounded-md"
                                          sideOffset={2}
                                        >
                                          <p className="tooltip-content text-white bg-[#505050] text-xs not-italic">
                                            {project}
                                          </p>
                                          <TooltipPrimitive.Arrow width={11} height={5} style={{ fill: '#505050' }} />
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={`${isPromptGenerating && 'not-clickable'}`}>
                      <div className="custom-select relative text-sm" id="transcript-dropdown">
                        <div className="custom-select-label relative flex items-center">
                          <label>Transcript :</label>
                          <div className="ml-1 flex items-center gap-2 cursor-pointer" onClick={toggleSelect}>
                            <div className="font-medium">{selectedTranscript}</div>
                            <div className="flex items-center justify-center h-3.5 w-3.5 mt-[3px]">
                              <img
                                className={`w-3.5 cursor-pointer transition-all duration-300 ease-[ease] ${
                                  selectOpen ? 'rotate-180' : ''
                                }`}
                                src="/icons/Down.svg"
                                alt="arrow"
                              />
                            </div>
                          </div>
                        </div>
                        {selectOpen && (
                          <ul className="absolute bg-[#FFFFFF] w-full top-8">
                            <li
                              className="w-full text-left px-6 py-2.5 hover:bg-[#FAFAFA] hover:text-[#a100ff] cursor-pointer"
                              onClick={() => handleSelectTranscript('Primary')}
                            >
                              Primary
                            </li>
                            <li
                              className="w-full text-left px-6 py-2.5 hover:bg-[#FAFAFA] hover:text-[#a100ff] cursor-pointer"
                              onClick={() => handleSelectTranscript('Secondary')}
                            >
                              Secondary
                            </li>
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="chatbot-container min-h-0 flex-grow px-6 pt-[110px]">
              {/* <img
                  src="/icons/chat-round.svg"
                  className="w-8 h-7 ml-4 cursor-pointer"
                  onClick={() => setShowResetModal(true)}
                /> */}
              <Chatbot
                config={config}
                messageParser={MessageParser}
                actionProvider={ActionProvider}
                placeholderText="Write your message (max 2,000 characters)"
                disableScrollToBottom
                validator={(input) => {
                  return true; // this should be true to trigger messageParser and input argusment will be empty due to custom chat-input box implementation.
                }}
              />
            </div>
            <div className="text-left text-sm pl-[72px] pr-0 py-0.5 mb:pl-8">{countChar} / 2000</div>
          </div>

          {/* Tabs */}
          <div
            ref={panelRef}
            className={`c-chat-response__rightwrapper bg-white tb:hidden max-w-[40vw] ${
              showRightPanel ? '-expand min-w-[24vw]' : 'min-w-[0vw]'
            }`}
            id="chatrightPane"
            style={{ width: showRightPanel ? `${panelWidth}%` : '0%' }}
          >
            <a
              href="javascript:;"
              id="menu-filter"
              role="button"
              className="c-chat-response__chat-btn-open btn-open-panel"
              onClick={handleRightPanel}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4.44853 13.9615L10.0514 8.48679L10.5581 7.99491L10.0773 7.58453L4.44853 2.08512C4.39027 2.00013 4.36374 1.89699 4.37367 1.79407C4.37367 1.64855 4.37367 1.57142 4.44853 1.50302C4.48888 1.45017 4.54068 1.40737 4.59995 1.37793C4.65923 1.34848 4.72439 1.33317 4.79043 1.33317C4.85647 1.33317 4.92164 1.34848 4.98091 1.37793C5.04018 1.40737 5.09198 1.45017 5.13233 1.50302L11.4291 7.6442C11.4291 7.6442 11.6709 7.86685 11.6666 8.03712C11.6623 8.20738 11.3887 8.43877 11.3887 8.43877L5.13233 14.5552C5.0327 14.6276 4.91312 14.6665 4.79043 14.6665C4.66775 14.6665 4.54816 14.6276 4.44853 14.5552C4.37447 14.4745 4.33331 14.3684 4.33331 14.2583C4.33331 14.1482 4.37447 14.0422 4.44853 13.9615Z"
                  fill="black"
                ></path>
              </svg>
            </a>
            <div
              className="resizer"
              id="resizer"
              title="Drag to resize"
              onMouseDown={handleMouseDown}
              style={{ cursor: 'ew-resize' }}
            >
              <div className="bar"></div>
            </div>
            <div className={`c-chat-response__rightwrapper-chatbtnpannel-container ${showRightPanel ? '-open' : ''}`}>
              <div
                role="tablist"
                aria-label="Tabs Label"
                className="c-tabs__wrap c-chat-response__rightwrapper-chatbtnpannel-container-tabwrapper"
              >
                <button
                  type="button"
                  className="c-tabs__btn"
                  role="tab"
                  aria-controls="tab0011"
                  aria-selected={activeChatTab === CHAT_TAB_VIEWS.RESPONSE_DETAILS ? 'true' : 'false'}
                  tabIndex={activeChatTab === CHAT_TAB_VIEWS.RESPONSE_DETAILS ? 0 : -1}
                  onClick={() => setActiveChatTab(CHAT_TAB_VIEWS.RESPONSE_DETAILS)}
                >
                  Matched Transcripts
                </button>
                <button
                  type="button"
                  className="c-tabs__btn"
                  role="tab"
                  aria-controls="tab0022"
                  aria-selected={activeChatTab === CHAT_TAB_VIEWS.STORYBUILDER ? 'true' : 'false'}
                  tabIndex={activeChatTab === CHAT_TAB_VIEWS.STORYBUILDER ? 0 : -1}
                  onClick={() => setActiveChatTab(CHAT_TAB_VIEWS.STORYBUILDER)}
                >
                  Storybuilder
                </button>
              </div>
              <div
                role="tabpanel"
                id="tab0011"
                className={activeChatTab === CHAT_TAB_VIEWS.RESPONSE_DETAILS ? '-show' : ''}
              >
                <ResponseDetails showRightPanel={showRightPanel} />
              </div>
              <div
                role="tabpanel"
                id="tab0022"
                className={activeChatTab === CHAT_TAB_VIEWS.STORYBUILDER ? '-show' : ''}
              >
                <div className=" mt-0 overflow-y-auto scrollbar-webkit !max-h-[calc(100vh-120px)]">
                  <StoryBuilder />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="loading-container flex flex-col justify-center items-center h-full w-full">
          <BounceLoader />
          <p className="mt-6 text-lg text-white">One Moment Please...</p>
        </div>
      )}
    </>
  );
};
